/**
 * @typedef {object} SizingUnitPreference
 * @property {string} market - Market handle the preference applies to
 * @property {boolean} showEu - Whether EU sizing is shown
 */

/** @type {SizingUnitPreference | null} */
let sizingUnitPreference = null;

try {
  sessionStorage.removeItem('product-option-sizing-unit');
} catch {
  // Ignore storage failures (private mode / quota)
}

if (!customElements.get('product-option-selector')) {
  customElements.define(
    'product-option-selector',
    class ProductOptionSelector extends HTMLElement {
      constructor() {
        super();

        /** @type {AbortController | null} */
        this.sizingToggleAbortController = null;
      }

      static get observedAttributes() {
        return ['data-current-market'];
      }

      connectedCallback() {
        this.bindSizingToggle();
      }

      disconnectedCallback() {
        this.sizingToggleAbortController?.abort();
      }

      /**
       * @param {string} name - Changed attribute name
       * @param {string | null} oldValue - Previous attribute value
       * @param {string | null} newValue - New attribute value
       */
      attributeChangedCallback(name, oldValue, newValue) {
        if (name !== 'data-current-market') return;
        if (oldValue == null || oldValue === newValue) return;

        this.resetToMarketDefault();
      }

      bindSizingToggle() {
        const toggle = this.querySelector('[data-sizing-toggle]');

        if (!(toggle instanceof HTMLInputElement)) return;

        this.sizingToggleAbortController?.abort();
        this.sizingToggleAbortController = new AbortController();
        const { signal } = this.sizingToggleAbortController;

        this.applySizingState(this.resolveSizingState());

        toggle.addEventListener(
          'change',
          (event) => {
            event.stopPropagation();
            this.applySizingState(toggle.checked);
            this.storeSizingState(toggle.checked);
          },
          { signal },
        );
      }

      /**
       * @returns {boolean} Whether the current market defaults to EU sizing
       */
      marketDefaultShowEu() {
        return (this.dataset.currentMarket || '') !== 'gb';
      }

      /**
       * @returns {boolean} Resolved EU sizing state for the current market
       */
      resolveSizingState() {
        const market = this.dataset.currentMarket || '';

        if (sizingUnitPreference?.market === market) {
          return sizingUnitPreference.showEu;
        }

        const showEu = this.marketDefaultShowEu();
        this.storeSizingState(showEu);

        return showEu;
      }

      resetToMarketDefault() {
        const showEu = this.marketDefaultShowEu();
        this.applySizingState(showEu);
        this.storeSizingState(showEu);
      }

      /**
       * @param {boolean} showEu - Whether to show EU sizing labels
       */
      applySizingState(showEu) {
        const toggle = this.querySelector('[data-sizing-toggle]');

        if (!(toggle instanceof HTMLInputElement)) return;

        toggle.checked = showEu;
        this.updateSizingLabels(showEu);
      }

      /**
       * @param {boolean} showEu - Whether to show EU sizing labels
       */
      updateSizingLabels(showEu) {
        const sizeGroups = this.querySelectorAll('.product-option-shoe-size');

        for (const group of Array.from(sizeGroups)) {
          if (!(group instanceof HTMLElement)) continue;

          const ukSize = group.querySelector('.product-option-shoe-size-uk');
          const euSize = group.querySelector('.product-option-shoe-size-int');

          group.dataset.unit = showEu ? 'eu' : 'uk';
          ukSize?.setAttribute('aria-hidden', showEu ? 'true' : 'false');
          euSize?.setAttribute('aria-hidden', showEu ? 'false' : 'true');
        }
      }

      /**
       * @param {boolean} showEu - Whether EU sizing is shown
       */
      storeSizingState(showEu) {
        sizingUnitPreference = {
          market: this.dataset.currentMarket || '',
          showEu,
        };
      }
    },
  );
}
